import React, {
  useState, useEffect, useRef,
} from 'react';
import clsx from 'clsx';
import useFollowUser from 'sdk/hooks/follow/useFollowUser';
import useUserIsFollowedByMe from 'sdk/hooks/follow/useUserIsFollowedByMe';
import { ReactComponent as IconPro } from 'images/shield-10.svg';
import Button from 'components/shared/Button';
import { Link, useNavigate } from 'react-router-dom';
import useExpertProfile from 'sdk/hooks/expert/useExpertProfile';
// eslint-disable-next-line no-use-before-define
import { LazyLoadImage } from 'react-lazy-load-image-component';
import styles from './ExpertView.module.scss';

let interval: NodeJS.Timeout | undefined

function Content({
  data, isFollowedByMe, onFollow, loading,
}: {
  data: any,
  isFollowedByMe?: boolean,
  onFollow: (e) => void,
  loading: boolean,
}) {
  return (
    <>
      <div className={styles.firstname}>
        <Link to={`/profile/${data?.username}`}>{data?.first_name}</Link>
      </div>
      <div className={styles.lastname}>
        <Link to={`/profile/${data?.username}`}>{data?.last_name}</Link>
      </div>

      <Button
        className={styles.btn}
        onClick={onFollow}
        loading={loading}
        wide
        secondary
      >
        {isFollowedByMe ? 'Following' : 'Follow'}
      </Button>
    </>
  )
}

function ExpertView({
  className,
  data,
  isNew,
  isActive,
}: {
  className?: string,
  data: any,
  isNew?: boolean,
  isActive?: boolean
}) {
  const [isFollowedByMe, setIsFollowedByMe] = useState(false)
  const followToUser = useFollowUser(data?.id)
  const {
    data: isFollowedByMeData,
    refetch: refetchIsFollowedByMe,
  } = useUserIsFollowedByMe(data?.id)
  const navigate = useNavigate()
  const videoRef = useRef<HTMLVideoElement>(null)
  const video = videoRef?.current
  const [, setVideoProgress] = useState(0)
  const { data: expertProfile } = useExpertProfile(data?.expertProfile?.id)

  const getProgress = () => (
    (video && video?.duration) ? (
      ((video?.currentTime || 0) / (video?.duration || 1)) * 100
    ) : 0
  )

  const startVideo = () => {
    if (video) {
      video.play()
      if (interval) clearInterval(interval)
      setVideoProgress(getProgress())
      interval = setInterval(() => setVideoProgress(getProgress()), 1000)
    }
  }

  const stopVideo = () => {
    if (video) {
      video.pause()
      setVideoProgress(getProgress())
      if (interval) clearInterval(interval)
    }
  }

  useEffect(() => () => {
    if (interval) clearInterval(interval)
  }, [])

  useEffect(() => {
    if (!isActive) {
      stopVideo()
    } else {
      startVideo()
    }
  }, [isActive])

  useEffect(() => {
    const videoEl = videoRef.current;
    return () => {
      if (videoEl) {
        videoEl.pause()
      }
    }
  }, [])

  useEffect(() => {
    setIsFollowedByMe(isFollowedByMeData || false)
  }, [isFollowedByMeData])

  const onFollow = async (e) => {
    e.stopPropagation()
    setIsFollowedByMe(true)
    followToUser.mutate(true, {
      onSuccess() {
        setIsFollowedByMe(true)
        refetchIsFollowedByMe()
      },
    })
  }

  const onUnFollow = async (e) => {
    e.stopPropagation()
    setIsFollowedByMe(false)
    followToUser.mutate(false, {
      onSuccess() {
        setIsFollowedByMe(false)
        refetchIsFollowedByMe()
      },
    })
  }

  const getMedia = (link: string) => {
    if (!link) {
      return ''
    }

    if (link.slice(-3) === 'mp4' || link.slice(-3) === 'mov' || link.slice(-3) === '3gp') {
      return <video ref={videoRef} src={link} muted loop />
    }

    return (
      <img
        src={link}
        alt={link}
      />
    )
  }

  if (isNew) {
    return (
      <div
        className={clsx(
          className,
          styles.containerNew,
        )}
        onClick={() => navigate(`/profile/${data?.username}`)}
      >
        <div className={styles.bg}>{getMedia(expertProfile?.bg_video || data?.bg_cover) }</div>
        <div className={styles.containerInfoTop} />

        <div className={styles.containerInfoBottom}>
          <Content
            data={data}
            isFollowedByMe={isFollowedByMe}
            loading={false}
            onFollow={isFollowedByMe ? onUnFollow : onFollow}
          />
        </div>
      </div>
    )
  }

  return (
    <div
      className={clsx(
        className,
        styles.container,
      )}
      onClick={() => navigate(`/profile/${data?.username}`)}
    >
      <div className={styles.avatar}>
        <div className={styles.avatarInner}>
          <LazyLoadImage
            src={data?.avatar}
            width="100%"
            height="100%"
            PlaceholderSrc={data?.avatar}
            effect="blur"
          />
        </div>
        <div className={styles.pro}><IconPro /></div>
      </div>

      <Content
        data={data}
        isFollowedByMe={isFollowedByMe}
        loading={false}
        onFollow={isFollowedByMe ? onUnFollow : onFollow}
      />
    </div>
  )
}

export default ExpertView
